import { AlertCircleIcon, BoxesIcon, CalendarIcon, ClipboardCheckIcon, LinkIcon, PackageIcon, RepeatIcon, SendIcon, ShieldAlertIcon, UserIcon } from 'lucide-react'
import { lastsWeeksOf } from '../../core/rules/subscriptionBuilder'
import { useLatestLoad } from '../../hooks/useLatestLoad'
import { fetchStock } from '../adminApi'
import { useAdmin } from '../AdminContext'
import { useInbox } from '../inbox/useInbox'
import { inboxOrderTotal } from '../inbox/inboxRules'
import { useCustomers } from '../customers/useCustomers'
import { attentionLabel, customerName } from '../customers/customerRules'
import { productChecks } from '../catalog/catalogRules'
import { countDue, locationsDue } from '../../core/rules/stockCount'
import { locationLabel } from '../../core/rules/stockMovements'
import { today } from '../../core/format/dates'
import { euro } from '../../core/format/currency'
import { plural } from '../../core/format/text'
import type { IconComponent } from '../../ui/icon'
import { inboxWaiting } from './inboxWaiting'

export type AttentionItem = {
  key: string
  to: string
  icon: IconComponent
  title: string
  meta?: string
  /** Wat ze daar gaat doen, als werkwoord ("Inpakken"). Staat rechts in de rij. */
  action: string
}

export type AttentionGroupKey = 'nu' | 'week' | 'winkel'
export type AttentionGroup = { key: AttentionGroupKey; title: string; hint: string; items: AttentionItem[] }

/** Zoveel losse rijen per soort; de rest wordt één regel "Nog 4 …". */
const PER_KIND = 4

/**
 * Wat er op Sarah wacht, in drie bakjes:
 * - **Nu**: klanten wachten erop (inpakken, afspraak bevestigen, vandaag posten);
 * - **Deze week**: kan vandaag of morgen (tellen, kuur koppelen, opvolgen, afmaken);
 * - **Winkel op orde**: de site blijft goed (uitverkocht, onvolledig, koppelingen).
 * Eén bron voor het overzicht; de regels zelf staan in inboxRules, customerRules, catalogRules en stockCount.
 */
export function useAttention() {
  const { user, catalog, index, issues, marketing } = useAdmin()
  const { inbox, status: inboxStatus } = useInbox()
  const { customers } = useCustomers()
  const { data: stock } = useLatestLoad(fetchStock)
  const day = today()

  const waiting = inbox ? inboxWaiting(inbox) : null
  const soldOut = catalog.products.filter((p) => p.kind !== 'giftcard' && p.kind !== 'kuur' && p.stock === 0)
  const countsDue = stock ? locationsDue(stock.items, stock.lastCountAt, day) : []
  const followUps = customers.filter((c) => c.attention.some((f) => f === 'geen-plan' || f === 'kuur-klaar'))
  const blocking = catalog.products.filter((p) => productChecks(p).some((c) => c.level === 'error'))
  // Slim bijvullen rekent met "gaat ± … mee"; zonder gaat zo'n product elke doos mee.
  const noLasts = catalog.products.filter((p) => p.kind === 'skincare' && lastsWeeksOf(p.usesPerBottle) === null)
  const posts = marketing.posts
    .filter((p) => p.date === day && p.status !== 'geplaatst' && p.status !== 'idee')
    .sort((a, b) => a.time.localeCompare(b.time))

  const capped = (items: AttentionItem[], rest: (n: number) => AttentionItem): AttentionItem[] =>
    items.length > PER_KIND ? [...items.slice(0, PER_KIND - 1), rest(items.length - (PER_KIND - 1))] : items

  const twoFactor: AttentionItem | null = user.twoFactor.enabled
    ? null
    : {
        key: 'two-factor',
        to: '/be/beveiliging',
        icon: ShieldAlertIcon,
        title: 'Tweestapsverificatie staat nog uit',
        meta: 'Beveilig je beheer met een app op je telefoon. Duurt twee minuten.',
        action: 'Aanzetten',
      }

  const now: AttentionItem[] = [
    ...(twoFactor && user.twoFactor.required ? [twoFactor] : []),
    ...capped(
      (waiting?.toPack ?? []).map((o) => ({
        key: o.id,
        to: '/be/inbox',
        icon: PackageIcon,
        title: `Bestelling van ${o.name}`,
        meta: `${euro(inboxOrderTotal(o))}${o.order.skinNote ? ` · “${o.order.skinNote}”` : ''}`,
        action: 'Inpakken',
      })),
      (n) => ({ key: 'pack-rest', to: '/be/inbox', icon: PackageIcon, title: `Nog ${plural(n, 'bestelling', 'bestellingen')} om in te pakken`, action: 'Naar de inbox' }),
    ),
    ...capped(
      (waiting?.bookingRequests ?? []).map((b) => ({
        key: b.id,
        to: b.customerId ? `/be/klanten/${b.customerId}?tab=afspraken` : '/be/inbox?tab=afspraken',
        icon: CalendarIcon,
        title: `${index.treatmentBySlug(b.booking.treatmentSlug)?.name ?? b.booking.treatmentSlug} voor ${b.booking.name}`,
        meta: `${b.change === 'verzet' ? 'Wil verzetten naar' : 'Aanvraag voor'} ${b.booking.date} om ${b.booking.time}${b.booking.firstVisit ? ' · eerste bezoek' : ''}`,
        action: b.change === 'verzet' ? 'Verzetten' : 'Bevestigen',
      })),
      (n) => ({ key: 'booking-rest', to: '/be/inbox?tab=afspraken', icon: CalendarIcon, title: `Nog ${plural(n, 'aanvraag', 'aanvragen')} voor een afspraak`, action: 'Naar de inbox' }),
    ),
    ...posts
      .filter((p) => p.status === 'klaar')
      .map((p) => ({
        key: p.id,
        to: `/be/social/${p.id}`,
        icon: SendIcon,
        title: p.title || 'Naamloze post',
        meta: `Vandaag ${p.time} · ${p.platforms.join(', ')}`,
        action: 'Posten',
      })),
  ]

  const week: AttentionItem[] = [
    ...(twoFactor && !user.twoFactor.required ? [twoFactor] : []),
    ...countsDue.map((l) => {
      const since = countDue(stock?.lastCountAt[l] ?? null, day).daysSince
      return {
        key: `telling-${l}`,
        to: '/be/voorraad?tab=tellen',
        icon: ClipboardCheckIcon,
        title: `Wekelijkse telling: ${locationLabel[l].toLowerCase()}`,
        meta: since === null ? 'Nog nooit helemaal geteld' : `Laatst geteld ${since} dagen geleden`,
        action: 'Tellen',
      }
    }),
    ...(waiting?.kuurToLink ?? []).map((o) => ({
      key: `kuur-${o.id}`,
      to: '/be/inbox',
      icon: LinkIcon,
      title: `Kuur van ${o.name}`,
      meta: 'Gekocht in de webshop, nog niet aan het dossier gekoppeld',
      action: 'Koppelen',
    })),
    ...capped(
      followUps.map((c) => {
        const flag = c.attention.includes('geen-plan') ? ('geen-plan' as const) : ('kuur-klaar' as const)
        return {
          key: `klant-${c.id}`,
          to: `/be/klanten/${c.id}?tab=${flag === 'geen-plan' ? 'huidplan' : 'afspraken'}`,
          icon: UserIcon,
          title: customerName(c),
          meta: attentionLabel[flag],
          action: 'Opvolgen',
        }
      }),
      (n) => ({ key: 'klant-rest', to: '/be/klanten', icon: UserIcon, title: `Nog ${plural(n, 'klant', 'klanten')} om op te volgen`, action: 'Naar klanten' }),
    ),
    ...posts
      .filter((p) => p.status !== 'klaar')
      .map((p) => ({
        key: p.id,
        to: `/be/social/${p.id}`,
        icon: SendIcon,
        title: p.title || 'Naamloze post',
        meta: `Staat voor vandaag ${p.time}, nog niet klaar`,
        action: 'Afmaken',
      })),
  ]

  const shop: AttentionItem[] = [
    ...(noLasts.length
      ? [
          {
            key: 'no-lasts',
            to: '/be/producten?filter=onvolledig',
            icon: RepeatIcon,
            title: `${plural(noLasts.length, 'product heeft', 'producten hebben')} nog geen “gaat ± … mee”`,
            meta: 'Nodig voor slim bijvullen: zonder gaat het product in elke doos mee.',
            action: 'Aanvullen',
          },
        ]
      : []),
    ...(blocking.length
      ? [
          {
            key: 'blocking',
            to: '/be/producten?filter=onvolledig',
            icon: AlertCircleIcon,
            title: `${plural(blocking.length, 'product mist', 'producten missen')} iets belangrijks`,
            meta: blocking
              .slice(0, 3)
              .map((p) => p.name)
              .join(' · '),
            action: 'Aanvullen',
          },
        ]
      : []),
    ...(soldOut.length
      ? [
          {
            key: 'sold-out',
            to: '/be/voorraad?filter=op',
            icon: BoxesIcon,
            title: `${plural(soldOut.length, 'product is', 'producten zijn')} online uitverkocht`,
            meta: soldOut
              .slice(0, 3)
              .map((p) => p.name)
              .join(' · '),
            action: 'Bekijken',
          },
        ]
      : []),
    ...(issues.length
      ? [
          {
            key: 'issues',
            to: '/be/huidvragen',
            icon: AlertCircleIcon,
            title: `${plural(issues.length, 'koppeling klopt', 'koppelingen kloppen')} niet`,
            meta: issues.slice(0, 2).join(' · '),
            action: 'Oplossen',
          },
        ]
      : []),
  ]

  const groups: AttentionGroup[] = [
    { key: 'nu', title: 'Nu', hint: 'Hier wacht een klant op.', items: now },
    { key: 'week', title: 'Deze week', hint: 'Vandaag of morgen is ook goed.', items: week },
    { key: 'winkel', title: 'Winkel op orde', hint: 'Zodat de site klopt.', items: shop },
  ]
  return { groups, urgent: now.length, total: now.length + week.length + shop.length, loading: !inbox && inboxStatus !== 'error' }
}
